import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Calendar, Clock, Users, Loader2, Check, CalendarDays,
  RefreshCw, ChevronRight, Video, User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit'
  });

const formatRelativeDate = (iso) => {
  const d = new Date(iso);
  const diffH = (d - Date.now()) / 3600000;
  if (diffH < 0) return 'Started';
  if (diffH < 1) return `In ${Math.round(diffH * 60)}min`;
  if (diffH < 24) return `In ${Math.floor(diffH)}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'Tomorrow';
  if (diffD < 7) return d.toLocaleDateString('en-GB', { weekday: 'long' });
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const LEVEL_COLORS = {
  A1: 'bg-[#4CAF50]/10 text-[#4CAF50]',
  A2: 'bg-[#8BC34A]/10 text-[#8BC34A]',
  B1: 'bg-[#F5A623]/10 text-[#F5A623]',
  B2: 'bg-[#FF9800]/10 text-[#FF9800]',
  C1: 'bg-[#1A2E6E]/10 text-[#1A2E6E]',
  C2: 'bg-[#D72B2B]/10 text-[#D72B2B]',
};

export const StudentAvailableClasses = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);

  useEffect(() => { fetchClasses(); }, []);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL + '/api/student/available-classes');
      setClasses(response.data);
    } catch (err) {
      console.error('Failed to fetch classes:', err);
      toast.error('Failed to load available classes');
    } finally {
      setLoading(false);
    }
  };

  const enrollInClass = async (classId) => {
    setEnrolling(classId);
    try {
      await axios.post(API_URL + '/api/student/classes/' + classId + '/enroll');
      toast.success('Successfully enrolled! Check "My Classes" to see it.');
      fetchClasses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enroll. Please try again.');
    } finally {
      setEnrolling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-available-classes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">
            Open Group Classes
          </h1>
          <p className="text-[#1A2E6E]/60 mt-1">
            Join a group session with open spots, or schedule a private lesson separately
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/student/schedule"
            className="flex items-center gap-2 px-4 py-2 border border-[#1A2E6E]/15 text-[#1A2E6E] rounded-xl hover:bg-[#1A2E6E]/5 text-sm font-medium transition-colors"
          >
            <CalendarDays size={14} /> Schedule 1:1
          </Link>
          <button
            onClick={fetchClasses}
            className="flex items-center gap-2 px-4 py-2 border border-[#E8EAF0] text-[#1A2E6E]/70 rounded-xl hover:bg-[#1A2E6E]/5 text-sm font-medium transition-colors w-fit"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {classes.length > 0 ? (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
          {classes.map((cls, index) => {
            const spotsLeft = cls.spots_available;
            const isFull = spotsLeft === 0;
            const isImminent = !isFull && (new Date(cls.scheduled_at) - Date.now()) < 3600000 * 2;
            const fillPct = Math.round(((cls.max_capacity - spotsLeft) / cls.max_capacity) * 100);

            return (
              <motion.div
                key={cls.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                data-testid={`open-class-card-${cls.id}`}
                className={`bg-white rounded-2xl border overflow-hidden flex flex-col transition-shadow hover:shadow-lg ${
                  isFull ? 'border-[#E8EAF0] opacity-70' : 'border-[#E8EAF0]'
                }`}
              >
                {/* Color banner */}
                <div className={`h-1.5 ${isFull ? 'bg-[#E8EAF0]' : 'bg-gradient-to-r from-[#1A2E6E] to-[#D72B2B]'}`} />

                <div className="p-5 flex-1 flex flex-col gap-3">
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-[#1A2E6E] text-base leading-tight">
                      {cls.title}
                    </h3>
                    {cls.level && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${LEVEL_COLORS[cls.level] || 'bg-[#1A2E6E]/10 text-[#1A2E6E]'}`}>
                        {cls.level}
                      </span>
                    )}
                  </div>

                  {cls.description && (
                    <p className="text-sm text-[#1A2E6E]/60 line-clamp-2">{cls.description}</p>
                  )}

                  {/* Details */}
                  <div className="space-y-2 text-sm text-[#1A2E6E]/70">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="shrink-0 text-[#D72B2B]" />
                      <span className="font-medium">{formatDate(cls.scheduled_at)}</span>
                      {!isFull && (
                        <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${
                          isImminent ? 'bg-[#F5A623]/10 text-[#F5A623]' : 'bg-[#1A2E6E]/5 text-[#1A2E6E]/50'
                        }`}>
                          {formatRelativeDate(cls.scheduled_at)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="shrink-0 text-[#1A2E6E]/40" />
                      {cls.duration_minutes} minutes
                    </div>
                    {cls.teacher_name && (
                      <div className="flex items-center gap-2">
                        <User size={14} className="shrink-0 text-[#1A2E6E]/40" />
                        {cls.teacher_name}
                      </div>
                    )}
                    {cls.meeting_link && (
                      <div className="flex items-center gap-2">
                        <Video size={14} className="shrink-0 text-[#4CAF50]" />
                        <span className="text-[#4CAF50] text-xs font-medium">Online class</span>
                      </div>
                    )}
                  </div>

                  {/* Capacity bar */}
                  <div className="mt-auto pt-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="flex items-center gap-1.5 text-xs text-[#1A2E6E]/50">
                        <Users size={12} />
                        {cls.max_capacity - spotsLeft}/{cls.max_capacity} enrolled
                      </span>
                      <span className={`text-xs font-semibold ${
                        isFull ? 'text-[#D72B2B]' : spotsLeft <= 2 ? 'text-[#F5A623]' : 'text-[#4CAF50]'
                      }`}>
                        {isFull ? 'Full' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                      </span>
                    </div>
                    <div className="h-1.5 bg-[#E8EAF0] rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          isFull ? 'bg-[#D72B2B]/40' : fillPct > 70 ? 'bg-[#F5A623]' : 'bg-[#4CAF50]'
                        }`}
                        style={{ width: `${fillPct}%` }}
                      />
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="px-5 pb-5">
                  <button
                    onClick={() => !isFull && enrollInClass(cls.id)}
                    disabled={enrolling === cls.id || isFull}
                    data-testid={`join-open-class-${cls.id}`}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-all ${
                      isFull
                        ? 'bg-[#E8EAF0] text-[#1A2E6E]/40 cursor-not-allowed'
                        : 'bg-[#1A2E6E] text-white hover:bg-[#2A4A8E] hover:shadow-md'
                    }`}
                  >
                    {enrolling === cls.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : isFull ? (
                      'Class Full'
                    ) : (
                      <>
                        <Check size={16} />
                        Join this class
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        /* Empty state */
        <div className="bg-white rounded-2xl border border-[#E8EAF0] overflow-hidden">
          <div className="p-10 text-center">
            <div className="w-20 h-20 bg-[#1A2E6E]/5 rounded-full flex items-center justify-center mx-auto mb-5">
              <CalendarDays className="w-10 h-10 text-[#1A2E6E]/30" />
            </div>
            <h3 className="text-lg font-bold text-[#1A2E6E] mb-2">No open classes right now</h3>
            <p className="text-[#1A2E6E]/60 text-sm max-w-sm mx-auto mb-8">
              Your teacher will add new classes soon. You'll be able to enroll directly from this page.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 max-w-md mx-auto">
              <Link
                to="/student/classes"
                className="flex items-center gap-3 p-4 rounded-xl border border-[#E8EAF0] hover:border-[#1A2E6E]/30 hover:bg-[#1A2E6E]/3 transition-all text-left"
              >
                <div className="w-10 h-10 bg-[#1A2E6E]/8 rounded-xl flex items-center justify-center shrink-0">
                  <Calendar className="w-5 h-5 text-[#1A2E6E]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A2E6E]">My Classes</p>
                  <p className="text-xs text-[#1A2E6E]/50">View your booked classes</p>
                </div>
                <ChevronRight size={16} className="ml-auto text-[#1A2E6E]/30" />
              </Link>

              <Link
                to="/student/schedule"
                className="flex items-center gap-3 p-4 rounded-xl border border-[#E8EAF0] hover:border-[#1A2E6E]/30 hover:bg-[#1A2E6E]/3 transition-all text-left"
              >
                <div className="w-10 h-10 bg-[#1A2E6E]/8 rounded-xl flex items-center justify-center shrink-0">
                  <CalendarDays className="w-5 h-5 text-[#1A2E6E]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#1A2E6E]">Schedule 1:1</p>
                  <p className="text-xs text-[#1A2E6E]/50">Book a private lesson with your teacher</p>
                </div>
                <ChevronRight size={16} className="ml-auto text-[#1A2E6E]/30" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAvailableClasses;
